import { getMailApiBase } from "@/auth/paths";
import { mailApiGet, mailApiPost } from "./client";

export type FetchEmlItem = {
  uid: number;
  stored?: boolean;
  skipped?: boolean;
  storeError?: string;
  byteSize?: number;
  messageDate?: string;
  includeAttachments?: boolean;
  message?: import("../types/mail").MailMessageDetail;
  error?: string;
};

export type FetchEmlResponse = {
  success: boolean;
  accountId: number;
  folder: string;
  storedCount?: number;
  attachmentsWarmed?: number;
  items: FetchEmlItem[];
};

export type WarmAttachmentsResponse = {
  success: boolean;
  accountId: number;
  folder: string;
  processedCount: number;
  attachmentsWarmed: number;
};

export type MailStorageSyncState = {
  active: boolean;
  done: number;
  total: number;
  label: string;
  folder: string;
  updatedAt: string;
};

export type MailStorageStats = {
  success: boolean;
  enabled: boolean;
  workerEnabled?: boolean;
  usedBytes: number;
  maxBytes: number;
  usedPercent: number;
  warnAtPercent: number;
  emlInboxMax: number;
  accountId?: number;
  emlUsedBytes?: number;
  emlMessageCount?: number;
  inboxCount?: number;
  inboxStored?: number;
  inboxTarget?: number;
  inboxMissing?: number;
  inboxBootstrapDone?: boolean;
  inboxArchiveUpToDate?: boolean;
  folders?: Array<{ folder: string; messageCount: number; usedBytes: number }>;
  sync?: MailStorageSyncState;
};

export type MailStoragePurgeResponse = {
  success: boolean;
  removed: number;
  freedBytes: number;
  emlUsedBytes: number;
  emlMessageCount: number;
  usedBytes: number;
  maxBytes: number;
  usedPercent: number;
};

export type MailStorageSyncProgress = {
  active: boolean;
  done: number;
  total: number;
  label: string;
};

export function fetchMailStorageStats(accountId: number) {
  return mailApiGet<MailStorageStats>(`storage/stats.php?accountId=${encodeURIComponent(String(accountId))}`);
}

export type EnqueueInboxArchiveResponse = {
  success: boolean;
  accountId: number;
  workerEnabled: boolean;
  enqueued: boolean;
  processedJobs: number;
  inboxStored: number;
  inboxTarget: number;
  inboxMissing: number;
  inboxArchiveUpToDate: boolean;
};

/** Enqueue archivage INBOX 200 via worker serveur (+ traitement inline immédiat). */
export function enqueueInboxArchive(accountId: number) {
  return mailApiPost<EnqueueInboxArchiveResponse>("storage/enqueue-inbox-archive.php", {
    accountId,
  });
}

export function fetchMailStoredUids(accountId: number, folder: string) {
  return mailApiGet<{ success: boolean; uids: number[] }>(
    `storage/stored-uids.php?accountId=${encodeURIComponent(String(accountId))}&folder=${encodeURIComponent(folder)}`
  );
}

export function fetchInboxArchiveUids(accountId: number) {
  return mailApiGet<{
    success: boolean;
    target: number;
    uids: number[];
    missing: number[];
    storedCount: number;
    missingCount: number;
  }>(`storage/inbox-archive-uids.php?accountId=${encodeURIComponent(String(accountId))}`);
}

export function fetchMailEmlBatch(params: {
  accountId: number;
  folder: string;
  uids: number[];
  includeAttachments?: boolean;
  warmAttachments?: boolean;
  /** Purge .eml serveur + retélécharge depuis IMAP. */
  force?: boolean;
  syncProgress?: MailStorageSyncProgress;
}) {
  if (params.uids.length === 0) {
    return Promise.resolve({
      ok: true as const,
      data: {
        success: true,
        accountId: params.accountId,
        folder: params.folder,
        storedCount: 0,
        items: [],
      },
    });
  }

  return mailApiPost<FetchEmlResponse>("imap/fetch-eml.php", {
    accountId: params.accountId,
    folder: params.folder,
    uids: params.uids,
    includeAttachments: params.includeAttachments !== false,
    warmAttachments: params.warmAttachments !== false,
    force: params.force === true,
    syncProgress: params.syncProgress,
  }, { timeoutMs: 120_000 });
}

export function warmMailAttachmentCacheBatch(params: {
  accountId: number;
  folder: string;
  uids: number[];
}) {
  if (params.uids.length === 0) {
    return Promise.resolve({
      ok: true as const,
      data: {
        success: true,
        accountId: params.accountId,
        folder: params.folder,
        processedCount: 0,
        attachmentsWarmed: 0,
      },
    });
  }

  return mailApiPost<WarmAttachmentsResponse>("storage/warm-attachments.php", {
    accountId: params.accountId,
    folder: params.folder,
    uids: params.uids,
  });
}

export function purgeMailEmlStorage(params: {
  accountId: number;
  purgeAll?: boolean;
  folder?: string;
  beforeDate?: string;
}) {
  return mailApiPost<MailStoragePurgeResponse>("storage/purge-eml.php", params);
}

export function relocateMailEmlStorage(params: {
  accountId: number;
  fromFolder: string;
  toFolder: string;
  uids: number[];
}) {
  return mailApiPost<{ success: boolean; moved: number }>("storage/relocate-eml.php", params);
}

export function getMailEmlExportUrl(params: {
  accountId: number;
  folder: string;
  uid: number;
  subject?: string;
}): string {
  const qs = new URLSearchParams({
    accountId: String(params.accountId),
    folder: params.folder,
    uid: String(params.uid),
  });
  if (params.subject?.trim()) {
    qs.set("subject", params.subject.trim());
  }
  return `${getMailApiBase()}/storage/export-eml.php?${qs.toString()}`;
}
